import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, subDays, startOfWeek, startOfMonth, startOfQuarter, startOfYear, differenceInDays } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarIcon, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { DateRange } from "react-day-picker";

export interface ReportDateRange {
  from: Date;
  to: Date;
}

interface Props {
  value: ReportDateRange;
  onChange: (range: ReportDateRange) => void;
  compareEnabled: boolean;
  onCompareToggle: (v: boolean) => void;
}

const presetKeys = [
  { key: "today", get: () => ({ from: new Date(), to: new Date() }) },
  { key: "thisWeek", get: () => ({ from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: new Date() }) },
  { key: "thisMonth", get: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
  { key: "thisQuarter", get: () => ({ from: startOfQuarter(new Date()), to: new Date() }) },
  { key: "thisYear", get: () => ({ from: startOfYear(new Date()), to: new Date() }) },
  { key: "last30Days", get: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { key: "last90Days", get: () => ({ from: subDays(new Date(), 90), to: new Date() }) },
];

export function getComparisonRange(range: ReportDateRange): ReportDateRange {
  const days = differenceInDays(range.to, range.from) + 1;
  return {
    from: subDays(range.from, days),
    to: subDays(range.from, 1),
  };
}

export default function ReportDateRangePicker({ value, onChange, compareEnabled, onCompareToggle }: Props) {
  const { t } = useTranslation("admin");
  const [open, setOpen] = useState(false);
  const [customRange, setCustomRange] = useState<DateRange | undefined>({ from: value.from, to: value.to });

  const label = useMemo(() => {
    const f = format(value.from, "d MMM yyyy", { locale: sv });
    const t = format(value.to, "d MMM yyyy", { locale: sv });
    if (f === t) return f;
    // Same year → skip year on from
    if (value.from.getFullYear() === value.to.getFullYear()) {
      return `${format(value.from, "d MMM", { locale: sv })} – ${t}`;
    }
    return `${f} – ${t}`;
  }, [value]);

  const applyPreset = (preset: typeof presetKeys[number]) => {
    const r = preset.get();
    onChange(r);
    setCustomRange({ from: r.from, to: r.to });
    setOpen(false);
  };

  const applyCustom = () => {
    if (customRange?.from && customRange?.to) {
      onChange({ from: customRange.from, to: customRange.to });
      setOpen(false);
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2 min-w-[220px] justify-start text-left font-normal">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{label}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            {/* Presets sidebar */}
            <div className="border-r p-3 space-y-1 min-w-[150px]">
              <p className="text-xs font-medium text-muted-foreground mb-2">{t("reports.datePicker.quickSelect")}</p>
              {presetKeys.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p)}
                  className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted transition-colors"
                >
                  {t(`reports.datePicker.presets.${p.key}`)}
                </button>
              ))}
              <div className="border-t my-2" />
              <p className="text-xs font-medium text-muted-foreground mb-1">{t("reports.datePicker.custom")}</p>
            </div>
            {/* Calendar */}
            <div className="p-3">
              <Calendar
                mode="range"
                selected={customRange}
                onSelect={setCustomRange}
                numberOfMonths={2}
                className="pointer-events-auto"
                locale={sv}
              />
              <div className="flex justify-end mt-2 gap-2">
                <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>{t("reports.datePicker.cancel")}</Button>
                <Button size="sm" onClick={applyCustom} disabled={!customRange?.from || !customRange?.to}>
                  {t("reports.datePicker.apply")}
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <div className="flex items-center gap-2">
        <Switch id="compare" checked={compareEnabled} onCheckedChange={onCompareToggle} />
        <Label htmlFor="compare" className="text-sm text-muted-foreground cursor-pointer flex items-center gap-1">
          <ArrowLeftRight className="h-3.5 w-3.5" />
          {t("reports.datePicker.comparePrevious")}
        </Label>
      </div>
    </div>
  );
}
